"use client";

import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Loader } from "lucide-react";
import HappeningCard from "./HappeningCard";
import { slugify } from "../../utils/helpers";

const HappeningList = ({
  items = [],
  title = "Happening Near You",
  subtitle,
  onLoadMore,
  isLoadingMore = false,
  hasMore = true
}) => {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScrollButtons = () => {
    if (!scrollContainerRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10); // 10px buffer
  };

  const handleScroll = () => {
    checkScrollButtons();

    // Check if we need to load more items
    if (!scrollContainerRef.current || !onLoadMore || isLoadingMore || !hasMore)
      return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;

    // Load more when user has scrolled 80% through the content
    if (scrollPercentage > 0.8) {
      onLoadMore();
    }
  };

  const scroll = direction => {
    if (!scrollContainerRef.current) return;

    const scrollAmount = 300;
    const newScrollLeft =
      direction === "left"
        ? scrollContainerRef.current.scrollLeft - scrollAmount
        : scrollContainerRef.current.scrollLeft + scrollAmount;

    scrollContainerRef.current.scrollTo({
      left: newScrollLeft,
      behavior: "smooth"
    });
  };

  // Use useEffect to set up scroll listener
  useEffect(
    () => {
      const container = scrollContainerRef.current;
      if (container) {
        container.addEventListener("scroll", handleScroll);
        // Initial check
        checkScrollButtons();

        return () => {
          container.removeEventListener("scroll", handleScroll);
        };
      }
    },
    [isLoadingMore, hasMore, onLoadMore]
  );

  const styles = {
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    msOverflowStyle: "none", // IE and Edge
    scrollbarWidth: "none" // Firefox
  };

  return (
    <div className="w-full pb-0">
      {/* Header with title and navigation buttons */}
      <div className="flex items-center justify-between mb-6 px-4 sm:px-6">
        <div>
          <h2 className="text-2xl font-bold text-black font-fraunces">
            {title}
          </h2>
          {subtitle && <p className="text-gray-600 mt-1">{subtitle}</p>}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => scroll("left")}
            disabled={!canScrollLeft}
            className={`p-2 rounded-full border ${
              canScrollLeft
                ? "bg-white text-gray-700 hover:bg-gray-100"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={() => scroll("right")}
            disabled={!canScrollRight}
            className={`p-2 rounded-full border ${
              canScrollRight
                ? "bg-white text-gray-700 hover:bg-gray-100"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Scrollable container */}
      <div
        ref={scrollContainerRef}
        className="flex overflow-x-auto gap-4 pb-4 px-4 sm:px-6 scrollbar-hide"
        style={styles}
      >
        {items.length > 0 ? (
          <>
            {items.map(item => (
              <div key={`${item.id}-${item.name}`} className="flex-shrink-0">
                <HappeningCard
                  image={item.cover_image}
                  imageAlt={item.imageAlt}
                  category={item.category}
                  title={item.name}
                  description={item.description}
                  location={item.location}
                  time={item.time}
                  date={item.date ?? item.event_date}
                  onGetDirection={item.onGetDirection}
                  link={`/eventdetail/${item.category}/${slugify(item.name)}/${
                    item.id
                  }`}
                />
              </div>
            ))}

            {/* Loading indicator */}
            {isLoadingMore && (
              <div className="flex-shrink-0 flex items-center justify-center w-32 h-64">
                <div className="flex flex-col items-center gap-2">
                  <Loader className="animate-spin h-6 w-6 text-gray-500" />
                  <span className="text-sm text-gray-500">Loading...</span>
                </div>
              </div>
            )}

            {/* End indicator */}
            {!hasMore &&
              items.length > 0 && (
                <div className="flex-shrink-0 flex items-center justify-center w-32 h-64">
                  <div className="text-center">
                    <div className="w-2 h-2 bg-gray-300 rounded-full mx-auto mb-2" />
                    <span className="text-xs text-gray-400">End of events</span>
                  </div>
                </div>
              )}
          </>
        ) : (
          <div className="w-full text-center py-10 text-gray-500">
            No events available at this time.
          </div>
        )}
      </div>
    </div>
  );
};

export default HappeningList;
